import CompletedTag from 'components/atoms/tags/CompletedTag';
import i18next from 'i18next';
import { tableSortMap } from 'infrastructure/antd/functions/create-sort-order';
import { useAppDispatch } from 'store';
import { capitalize } from 'utils';
import { DateFormatter } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import { appointmentMethodMap } from 'constants/common';

import { buildPatientInfo } from '../utils/patient-info';
import { renderPatientCell } from '../patient-cell';
import DueDateCell from '../due-date-cell';
import NoteCell from '../note-cell';
import s from './styles.module.scss';
import NotePreview from '../note-preview';

import type { IBaseTableColumns } from 'components/atoms/base-table/types';
import type {
  IFollowUpTableData,
  IFollowUpTableDataFilters,
  IKeyNoteData,
} from 'infrastructure/interfaces';

interface Props {
  filter: IFollowUpTableDataFilters;
}

const COMPLETED_SECONDS = 60 * 60 - 1;

export const useColumns = ({
  filter,
}: Props): IBaseTableColumns<IFollowUpTableData> => {
  const {
    ccmConsole: { setTableNote, toggleOpenDrawer },
  } = useAppDispatch();

  const renderPatientCellContent = (record: IFollowUpTableData) => {
    const showCompletedTag =
      (record.ccmActionTimes ?? 0) > COMPLETED_SECONDS &&
      record.ccmInteractionStatus &&
      record.hasOnlyMessageResponse !== true;

    return renderPatientCell({
      record,
      headingRightSlot: showCompletedTag ? <CompletedTag small /> : undefined,
      headingWrapperClassName: s['followup-patient-tag-wrapper'],
    });
  };

  return [
    {
      label: i18next.t('labels.patient'),
      key: 'name',
      width: '25%',
      align: 'left',
      dataCy: 'follow-up-patients-name',
      sortable: true,
      sortKey: 'name',
      sortOrder: filter['f-name'] ? tableSortMap[filter['f-name']] : undefined,
      render(_name: string, record) {
        return renderPatientCellContent(record);
      },
    },
    {
      label: i18next.t('labels.provider'),
      key: 'provider',
      width: '15%',
      align: 'left',
      dataCy: 'follow-up-patients-provider',
      sortable: true,
      sortOrder: filter['f-provider']
        ? tableSortMap[filter['f-provider']]
        : undefined,
      render(provider: string) {
        return provider;
      },
    },
    {
      label: i18next.t('labels.callsMonth'),
      key: 'callsMonth',
      align: 'left',
      dataCy: 'follow-up-patients-calls-month',
      sortable: true,
      sortOrder: filter['f-callsMonth']
        ? tableSortMap[filter['f-callsMonth']]
        : undefined,
    },
    {
      label: i18next.t('labels.dueDate'),
      key: 'dueDate',
      align: 'left',
      dataCy: 'follow-up-patients-due-date',
      sortable: true,
      sortOrder: filter['f-dueDate']
        ? tableSortMap[filter['f-dueDate']]
        : undefined,
      render(dueDate: string | null, record) {
        return <DueDateCell date={dueDate} patientGuid={record.guid} />;
      },
    },
    {
      label: i18next.t('labels.contactMethod'),
      key: 'contactMethod',
      align: 'left',
      dataCy: 'follow-up-patients-contact-method',
      render(contactMethod?: keyof typeof appointmentMethodMap | string) {
        if (!contactMethod) return '';
        const normalized =
          appointmentMethodMap[
            contactMethod as keyof typeof appointmentMethodMap
          ];
        return normalized ?? contactMethod;
      },
    },
    {
      label: i18next.t('labels.interactiveTime'),
      key: 'actionTimes',
      align: 'left',
      dataCy: 'follow-up-patients-interactive-time',
      sortable: true,
      sortOrder: filter['f-actionTimes']
        ? tableSortMap[filter['f-actionTimes']]
        : undefined,
    },
    {
      label: i18next.t('labels.ccmInteractionStatus'),
      key: 'ccmInteractionStatus',
      align: 'left',
      dataCy: 'follow-up-patients-ccmInteractionStatus',
      sortable: true,
      sortOrder: filter['f-ccmInteractionStatus']
        ? tableSortMap[filter['f-ccmInteractionStatus']]
        : undefined,
      render(ccmInteractionStatus: boolean) {
        return capitalize(String(ccmInteractionStatus));
      },
    },
    {
      label: i18next.t('labels.ccmAppt'),
      key: 'appointmentDate',
      align: 'left',
      dataCy: 'follow-up-patients-appointment-date',
      sortable: true,
      sortOrder: filter['f-appointmentDate']
        ? tableSortMap[filter['f-appointmentDate']]
        : undefined,
      render(appointmentDate: string) {
        return DateFormatter({
          date: appointmentDate,
          format: DateFormats['MM/dd/yyyy'],
        });
      },
    },
    {
      label: i18next.t('labels.notes'),
      key: 'notes',
      width: '10%',
      align: 'left',
      render(notes: IKeyNoteData[] = []) {
        return <NotePreview notes={notes} />;
      },
    },
    {
      label: '',
      key: 'noteaction',
      width: '3%',
      align: 'left',
      render(_unused: IKeyNoteData[] = [], record) {
        const noteList = record.notes ?? [];
        return (
          <NoteCell
            length={noteList.length}
            onClick={() => {
              setTableNote({
                notes: noteList,
                tableName: 'follow-up',
                patientGuid: record.guid,
                patientName: record.name,
                patientInfo: buildPatientInfo(record),
              });
              toggleOpenDrawer();
            }}
          />
        );
      },
      tableCollClassName: s['note-cell'],
      dataCy: 'follow-up-patients-notes',
    },
  ];
};
